// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import { push } from "connected-react-router";
import type { Action } from "redux-action";

import http from "@http";
import { route } from "@routes/helper";
import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import { transformError } from "@transformers/response";
import {
  GetAbsenceMonitoringList,
  GetAbsenceMonitoring,
  GetAbsenceMonitoringSchema,
  AddAbsenceMonitoring,
  UpdateAbsenceMonitoring,
  UpdateAbsenceMonitoringAssignments,
  DisableAbsenceMonitoring,
  AddNewAppealProcess,
  UpdateAppealProcess,
} from "@modules/service/constants/actions";
import { delay } from "@sagas/utils";
import { dataURLtoFile } from "@utils/file";

import type {
  ApprovableContainer,
  HierarchyAssignment,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

const normalizeReason = r => ({
  id: r?.id || null,
  name: r?.name || r?.reason || "",
  is_active: Boolean(r?.is_active || r?.isActive || false),
});

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type AbsenceMonitoringDTO = AbsenceMonitoring & {
  appeal: Object,
  appealReasons: Object,
  outcomeReasons: Object,
};

// READ

type AbsenceMonitoringListRequest = $Exact<{
  clientId: string,
  isActive: null | boolean,
}>;

export function* onGetAbsenceMonitoringList({
  payload,
}: Action<AbsenceMonitoringListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<ApprovableContainer<AbsenceMonitoring>>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process`,
      {
        params: { isActive: payload.isActive },
      },
    );

    yield put(actionCreators.GetAbsenceMonitoringListSuccess(response.data.content));
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringListError(transformError(e)));
  }
}

type AbsenceMonitoringRequest = AbsenceMonitoringListRequest &
  $Exact<{
    processId: string,
    id: string,
  }>;

export function* onGetAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<AbsenceMonitoring>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.processId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/assignment`,
    );

    const monitoringData = !stableResponse.data.appealConfig
      ? stableResponse.data
      : {
          ...stableResponse.data,
          content: {
            ...stableResponse.data.content,
            appeal: {
              id: stableResponse.data.appealConfig.id,
              isActive: stableResponse.data.appealConfig.isActive,
              kpiDays: stableResponse.data.appealConfig.kpiDays,
              instruction: stableResponse.data.appealConfig.instruction,
              appealForm: stableResponse.data.appealConfig.appealForm?.url,
            },
            outcomeReasons: stableResponse.data.appealConfig.outcomeReasons,
            appealReasons: stableResponse.data.appealConfig.appealReasons,
          },
        };

    yield all([
      put(actionCreators.GetAbsenceMonitoringSuccess(monitoringData)),
      put(actionCreators.SetAbsenceMonitoringAssignments(assignmentsResponse.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringError(transformError(e)));
  }
}

type RefreshRequest = $Exact<{
  clientId: string,
  processId?: string,
  isActive?: boolean | null,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  yield put(
    actionCreators.GetAbsenceMonitoringList({
      clientId: params.clientId,
      isActive: params.isActive,
    }),
  );

  if (params.processId) {
    yield put(
      actionCreators.GetAbsenceMonitoring({
        clientId: params.clientId,
        processId: params.processId,
        isActive: params.isActive,
      }),
    );
  }
}

// CREATE

export function* onGetAbsenceMonitoringSchema({
  payload,
}: Action<AbsenceMonitoringListRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw new Error("Invalid payload provided");
    }
    const schema = yield parseSchema("AbsenceMonitoringProcessRevisionDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAbsenceMonitoringSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAbsenceMonitoringSchemaError(transformError(e)));
  }
}

type AbsenceMonitoringAddRequest = AbsenceMonitoringListRequest & {
  draft: AbsenceMonitoringDTO,
  assignments: Array<HierarchyAssignment>,
  isActive: null | boolean,
  appeal: Object,
};

export function* onAddAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringAddRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = {
      ...payload.draft,
    };
    delete draft.id;
    delete draft.appeal;
    delete draft.appealReasons;
    delete draft.outcomeReasons;

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process`,
      draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield put(
      actionCreators.AddNewAppealProcess({
        clientId: payload.clientId,
        appeal: payload.appeal,
        processId: response.data.id,
        isActive: payload.isActive,
      }),
    );
    yield put(actionCreators.AddAbsenceMonitoringSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.AddAbsenceMonitoringError(transformError(e)));
  }
}

// UPDATE

type AbsenceMonitoringUpdateRequest = AbsenceMonitoringRequest & {
  draft: AbsenceMonitoringDTO,
};

export function* onUpdateAbsenceMonitoring({
  payload,
}: Action<AbsenceMonitoringUpdateRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = {
      ...payload.draft,
    };
    delete draft.id;
    delete draft.appeal;
    delete draft.appealReasons;
    delete draft.outcomeReasons;

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}`,
      draft,
    );

    yield put(actionCreators.UpdateAbsenceMonitoringSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceMonitoringError(e));
  }
}

type AbsenceMonitoringUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<AbsenceMonitoring>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateAbsenceMonitoringAssignments({
  payload,
}: Action<AbsenceMonitoringUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const prevAssignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/absence-monitoring/process/${payload.container.id}/assignment`,
    );
    if (prevAssignmentsResponse.data && prevAssignmentsResponse.data.length) {
      yield all([
        ...prevAssignmentsResponse.data.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/absence-monitoring/process/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/absence-monitoring/process/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      processId: payload.container.id,
    });
  } catch (e) {
    yield put(actionCreators.UpdateAbsenceMonitoringAssignmentsError(transformError(e)));
  }
}

// DISABLE

export type AbsenceMonitoringDisableRequest = AbsenceMonitoringRequest &
  $Exact<{
    content: AbsenceMonitoring,
  }>;

export function* onDisable({
  payload,
}: Action<AbsenceMonitoringDisableRequest>): Generator<
  *,
  void,
  ItemResponse<ApprovableContainer<AbsenceMonitoring>>,
> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.content) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const draft = payload.content;

    if (!draft) {
      throw Error("Invalid draft");
    }

    yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}`,
      {
        ...draft,
        activeFrom: draft.activeTo ? new Date().toISOString() : draft.activeFrom,
        activeTo: draft.activeTo ? null : new Date().toISOString(),
      },
    );

    yield all([
      call(delay, 1000),
      put(
        push(
          route("ADMINISTRATION_SERVICE_FEATURES_MONITOR", {
            clientId: payload.clientId,
          }),
        ),
      ),
    ]);
    yield put(sendSuccessMessage("DRAFT_UPDATED"));
  } catch (e) {
    yield put(actionCreators.DisableAbsenceMonitoringError(transformError(e)));
  }
}

// APPEALS
export function* onAddNewAppealProcess({ payload }: Action<*>): Generator<*, void, *> {
  try {
    console.log("payload", payload);
    if (!payload || !payload.clientId || !payload.processId || !payload.appeal) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = new FormData();
    data.append(
      "appealConfig",
      new Blob(
        [
          JSON.stringify({
            is_active: Boolean(payload.appeal.form.isActive),
            instruction: payload.appeal.form.instruction,
            kpi_days: Number(payload.appeal.form.kpiDays),
            appeal_reasons: (payload.appeal.appealReasons || []).map(normalizeReason),
            outcome_reasons: (payload.appeal.outcomeReasons || []).map(normalizeReason),
          }),
        ],
        { type: "application/json" },
      ),
    );

    if (payload.appeal.form.appealForm) {
      const { blob, name } = dataURLtoFile(payload.appeal.form.appealForm);
      data.append("appealFormFile", blob, name);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/appeal`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.AddNewAppealProcessSuccess()),
      call(onRefresh, {
        clientId: payload.clientId,
        processId: payload.processId,
        isActive: payload.isActive,
      }),
      put(sendSuccessMessage("ABSENCE_MONITORING_PROCESS_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddNewAppealProcessError(e));
  }
}

export function* onUpdateAppealProcess({ payload }: Action<*>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.processId || !payload.appeal) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = new FormData();
    data.append(
      "appealConfig",
      new Blob(
        [
          JSON.stringify({
            is_active: Boolean(payload.appeal.form.isActive),
            instruction: payload.appeal.form.instruction,
            kpi_days: Number(payload.appeal.form.kpiDays),
            appeal_reasons: (payload.appeal.appealReasons || []).map(normalizeReason),
            outcome_reasons: (payload.appeal.outcomeReasons || []).map(normalizeReason),
          }),
        ],
        { type: "application/json" },
      ),
    );

    const appealForm = payload.appeal.form.appealForm;
    if (appealForm && appealForm.startsWith("data:")) {
      const { blob, name } = dataURLtoFile(payload.appeal.form.appealForm);
      data.append("appealFormFile", blob, name);
    }

    // means that we want to remove currently existing file
    if (!appealForm) {
      data.append("appealFormFile", new Blob([], { type: "application/octet-stream" }), "null");
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/absence-monitoring/process/${payload.processId}/appeal/${payload.processId}`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    yield all([
      put(actionCreators.UpdateAppealProcessSuccess()),
      call(onRefresh, {
        clientId: payload.clientId,
        processId: payload.processId,
        isActive: payload.isActive,
      }),
      put(sendSuccessMessage("ABSENCE_MONITORING_PROCESS_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAppealProcessError(e));
  }
}

// ROOT

export function* AbsenceMonitoringSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceMonitoringList, onGetAbsenceMonitoringList),
    takeEvery(GetAbsenceMonitoring, onGetAbsenceMonitoring),
    takeEvery(GetAbsenceMonitoringSchema, onGetAbsenceMonitoringSchema),
    takeEvery(AddAbsenceMonitoring, onAddAbsenceMonitoring),
    takeEvery(UpdateAbsenceMonitoring, onUpdateAbsenceMonitoring),
    takeEvery(UpdateAbsenceMonitoringAssignments, onUpdateAbsenceMonitoringAssignments),
    takeEvery(DisableAbsenceMonitoring, onDisable),
    takeEvery(AddNewAppealProcess, onAddNewAppealProcess),
    takeEvery(UpdateAppealProcess, onUpdateAppealProcess),
  ]);
}

export default AbsenceMonitoringSagas;
